import { useEffect, useState } from "react";
import { onCelebrate } from "@/lib/celebrate";

/**
 * Game-style celebration overlay. Listens for `celebrate(...)` events and
 * shows a brief full-screen confetti + headline. Auto-dismisses after ~2.2s.
 */
export const CelebrationOverlay = () => {
  const [active, setActive] = useState(null); // { emoji, title, subtitle, key }

  useEffect(() => {
    const off = onCelebrate((payload) => {
      setActive({ ...payload, key: Date.now() });
      window.setTimeout(() => setActive(null), 2200);
    });
    return off;
  }, []);

  if (!active) return null;

  // Generate 24 confetti particles with randomized props
  const particles = Array.from({ length: 24 }, (_, i) => {
    const left = Math.random() * 100;
    const delay = Math.random() * 0.35;
    const dur = 1.4 + Math.random() * 0.9;
    const rot = Math.random() * 360;
    const drift = -40 + Math.random() * 80;
    const emoji = ["🎉", "✨", "🎊", "💸", "💎", "🥇"][i % 6];
    return { i, left, delay, dur, rot, drift, emoji };
  });

  return (
    <div
      className="fixed inset-0 z-[200] pointer-events-none flex items-start justify-center pt-[18vh]"
      data-testid="celebration-overlay"
      key={active.key}
    >
      {/* Confetti */}
      <div className="absolute inset-0 overflow-hidden">
        {particles.map((p) => (
          <span
            key={p.i}
            className="absolute text-2xl"
            style={{
              left: `${p.left}%`,
              top: "-10vh",
              animation: `wfw-confetti-fall ${p.dur}s cubic-bezier(.2,.7,.4,1) ${p.delay}s forwards`,
              transform: `rotate(${p.rot}deg)`,
              ["--wfw-drift"]: `${p.drift}px`,
            }}
          >
            {p.emoji}
          </span>
        ))}
      </div>

      {/* Headline card */}
      <div
        className="relative bg-white border-2 border-black px-7 py-5 shadow-[8px_8px_0_0_rgba(0,0,0,0.9)] animate-[wfw-celebrate-pop_320ms_cubic-bezier(.34,1.56,.64,1)_both]"
        data-testid="celebration-card"
      >
        <div className="text-center">
          <div className="text-5xl mb-1 leading-none" data-testid="celebration-emoji">{active.emoji}</div>
          <div className="font-heading text-2xl font-black tracking-tight" data-testid="celebration-title">
            {active.title}
          </div>
          {active.subtitle && (
            <div className="text-xs mt-1 text-neutral-700 font-medium" data-testid="celebration-subtitle">
              {active.subtitle}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes wfw-confetti-fall {
          0%   { transform: translateY(0) translateX(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(110vh) translateX(var(--wfw-drift, 0px)) rotate(720deg); opacity: 0.85; }
        }
        @keyframes wfw-celebrate-pop {
          0%   { transform: scale(0.6) rotate(-3deg); opacity: 0; }
          60%  { transform: scale(1.05) rotate(1deg); opacity: 1; }
          100% { transform: scale(1) rotate(0deg); opacity: 1; }
        }
      `}</style>
    </div>
  );
};
